/*
 * File: FormModel.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: MST model for the form builder to adhere to.
 *  Any form model built to use with formbuilder will need to follow these props types
 */

import { types } from "mobx-state-tree";
import OptionsModel from "./OptionsModel";

const FormModel = types
  .model("FormModel", {
    type: types.string,
    name: types.string,
    label: types.optional(types.string, ""),
    id: types.optional(types.number, 0),
    placeholder: types.optional(types.string, ""),
    required: types.optional(types.boolean, false),
    disabled: types.optional(types.boolean, false),
    options: types.optional(types.array(OptionsModel), []),
    optionKey: types.maybe(types.string),
    rows: types.optional(types.number, 3),
    isBool: types.optional(types.boolean, false),
    isCustomError: types.optional(types.boolean, false),
    errorMessage: types.optional(types.frozen(types.frozen), null),
    materialInputProps: types.optional(types.frozen(types.frozen), {}),
    htmlInputProps: types.optional(types.frozen(types.frozen), {}),
    gridItemProps: types.optional(types.frozen(types.frozen), {
      xs: 12,
      sm: false
    }),
    hidden: types.optional(types.boolean, false),
    formControlLabel: types.maybe(types.string),
    checked: types.optional(types.boolean, false)
  })
  .actions(self => ({
    /**
     * Sets a custom validation error for the field, or removes a custom validation error. Note that custom validation errors will stop the default validations from running until the custom error is removed.
     *
     * @param {string | null | undefined} message The error message to use, or null/undefined if the error should be cleared.
     */
    updateErrorMessage(message) {
      self.isCustomError = message != null;
      self.errorMessage = message;
    },
    /**
     * Called on each field on blur
     *
     * @param {Object} data: data submitted from the form
     */
    validateFieldForBlur(data) {
      // If a custom error was set, we don't want to overwrite it.
      if (self.isCustomError === true) {
        return;
      }

      self.errorMessage = null;
      const value = self.getFieldValue(data);

      // validate data
      self._checkEmail(value);
      if (self.type === "phone") {
        self._checkPhoneLength(value);
      }
    },

    /**
     * Called on each field after the form has been submitted
     *
     * @param {Object} data: data submitted from the form
     */
    validateFieldForSubmit(data) {
      // If a custom error was set, we don't want to overwrite it.
      if (self.isCustomError === true) {
        return;
      }

      self.errorMessage = null;
      const value = self.getFieldValue(data);

      // validate data
      self._checkRequired(value);
      if (self.type === "phone") {
        self._checkPhoneObject(value);
      }
    },

    getFieldValue(data) {
      let value = data[self.name];
      if (value && typeof value === "string") {
        value = value.trim();
      }
      return value;
    },

    setFormOptions(val) {
      self.options = val;
    },

    setHidden(hidden) {
      self.hidden = hidden;
    },

    setDisabled(disabled) {
      self.disabled = disabled;
    },

    /**
     * Makes sure there is a value if the field is required
     *
     * @param {String} value
     */
    _checkRequired(value) {
      if (self.required === true) {
        let isValid = true;
        if ((self.type === "select" && value === "0") || value === "") {
          isValid = false;
        } else if (
          self.name === "accountProvider" &&
          self.hidden === false &&
          (value.userId === undefined || isNaN(value.userId))
        ) {
          isValid = false;
        } else if (!value) {
          isValid = false;
        }
        if (!isValid) self.errorMessage = "Required field";
      }
    },

    /**
     * Validates email by checking for the '@' and '.' in the string
     *
     * Javascript email validation is not 100%
     *
     * @param {String} email
     */
    _checkEmail(email) {
      if (self.type === "email" && email) {
        let regEx = /\S+@\S+\.\S+/;

        if (!regEx.test(email)) {
          self.errorMessage = "Invalid email";
        }
      }
    },

    /**
     * Validates that the phone number is a 10-digit number by stripping out everything
     * but numbers
     *
     * @param {String} phone
     */
    _checkPhoneLength(phone) {
      if (!phone) {
        return;
      }
      let phoneNumber = phone.number;
      if (phoneNumber && phoneNumber.replace(/\D/g, "").length !== 10) {
        self.errorMessage = "Number needs to be 10 digits";
      }
    },

    /**
     * Checks if there's a phone number without a type.
     *
     * @param {String} phone
     */
    _checkPhoneObject(phone) {
      if (!phone) {
        return;
      }
      const phoneNumber = phone.number;
      const phoneType = phone.type;
      if (phoneNumber && phoneType === undefined) {
        self.errorMessage = "Phone number requires a phone type";
      } else {
        self.errorMessage = null;
      }
    }
  }));

export default FormModel;
